import TextArea from 'antd/lib/input/TextArea'
import classes from '../PromptGeneration/PromptGeneration.module.less'
import Flex from 'antd/lib/flex'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, FormInstance, Input, Radio, Space } from 'antd/lib'
import { InfoCircleFilled, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { Loader } from 'Components/common/Loader/Loader'
import {
    ContainerDataType,
    ContainerType,
    CustomDataType,
    PromptGenerationFromContextFormType,
} from 'Types/Tasks.types'
import { useForm } from 'antd/lib/form/Form'
import notification from 'antd/lib/notification'
import parse from 'html-react-parser'
import { MarkdownViewer } from '../MarkdownViewer/MarkdownViewer'
import { TransformedModelFeedback } from 'Types/CustomTask.types'
import { getTransformedModelFeedback, streamModelResponse } from 'Utils/customTask.utils'
import { ModelFeedback } from '../ModelFeedback/ModelFeedback'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { PlagiarismChecker } from 'Tasks/CustomTask/UI/common/PlagiarismChecker/PlagiarismChecker'
import MarkdownPreviewComponent from '../MarkdownPreviewComponent/MarkdownPreviewComponent'

const RETRY_LIMIT = 5

type PromptGenerationProps = {
    initialValues?: PromptGenerationFromContextFormType
    promptInstruction: string
    onPromptRevisionCreation: (
        type: 'PROMPT_REVISION' | 'RETRY_NON_TURN'
    ) => Promise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>> | undefined>
    setIsGeneratedPromptValid: (val: boolean) => void
    setModelResponseExtraInfo: React.Dispatch<
        React.SetStateAction<{
            totalModelResponsesOnPromptRevision: number
            modelResponseFeedbackCode: {
                value: number
                label: string
            }[]
            modelResponse: string
        }>
    >
    setForms: (forms: {
        promptGenerationFromContextForm?: FormInstance<PromptGenerationFromContextFormType>
    }) => void
    installedPlugins: IPlugin[]
    outputStartState?: CustomDataType
    onContainerUpdate?: (container: ContainerDataType<ContainerType.Custom, CustomDataType>) => void
}

export const PromptGenerationWithContext = (props: PromptGenerationProps) => {
    const {
        initialValues,
        promptInstruction,
        onPromptRevisionCreation,
        setIsGeneratedPromptValid,
        setModelResponseExtraInfo,
        setForms,
        installedPlugins,
        outputStartState,
    } = props
    const [form] = useForm<PromptGenerationFromContextFormType>()
    const [isCorrectResponse, setIsCorrectResponse] = useState<boolean>()
    const [fetchingModelResponse, setFetchingModelResponse] = useState(false)
    const [modelOutput, setModelOutput] = useState<{
        modelResponse: string | undefined
        modelFeedback: TransformedModelFeedback[]
        isStreamingCompleted: boolean
        hasError: boolean
    }>({
        modelResponse: undefined,
        modelFeedback: [],
        isStreamingCompleted: false,
        hasError: false,
    })
    const promptFormValue = Form.useWatch('prompt', form)
    const [notificationApi, contextHolder] = notification.useNotification()
    const [incorrectResponseCount, setIncorrectResponseCount] = useState<number>(0)
    const abortControllerRef = useRef<AbortController | null>(null)
    const [isPlagiarismCheckPassed, setIsPlagiarismCheckPassed] = useState<boolean>(true)

    useEffect(() => {
        setForms({ promptGenerationFromContextForm: form })
        if (installedPlugins.find(plugin => plugin.id === 'Plagiarism')) {
            setIsPlagiarismCheckPassed(false)
        }
        return () => {
            abortControllerRef.current?.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (outputStartState?.response.main_info.value.prompt) {
            setIsCorrectResponse(false)
            setModelOutput({
                modelResponse:
                    outputStartState.response.main_info.value.prompt_revisions?.at(-1)
                        ?.steps_annotation,
                modelFeedback: getTransformedModelFeedback(
                    outputStartState.response.main_info.value.prompt_revisions?.at(-1)
                        ?.feedback_from_model || []
                ),
                isStreamingCompleted: true,
                hasError: false,
            })
        }
    }, [
        outputStartState?.response.main_info.value.prompt,
        outputStartState?.response.main_info.value.prompt_revisions,
    ])

    const onTestModelResponse = async () => {
        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller

        setModelOutput({
            modelFeedback: [],
            modelResponse: undefined,
            isStreamingCompleted: false,
            hasError: false,
        })
        setIsCorrectResponse(undefined)
        setFetchingModelResponse(true)

        const reader =
            (await onPromptRevisionCreation(
                incorrectResponseCount === 0 || isCorrectResponse
                    ? 'PROMPT_REVISION'
                    : 'RETRY_NON_TURN'
            )) ?? null
        setFetchingModelResponse(false)

        await streamModelResponse({
            reader,
            signal: controller.signal,
            onModelMessage: message => {
                setModelOutput(prev => ({
                    ...prev,
                    modelResponse: message,
                    isStreamingCompleted: false,
                }))
            },
            onModelFeedback: feedback => {
                setModelOutput(prev => ({ ...prev, modelFeedback: feedback }))
                setIncorrectResponseCount(prev => (isCorrectResponse ? 1 : prev + 1))
            },
            onFinalResponse: (feedback, submission, modelResponse) => {
                // Update local draft container with the response from backend
                props.onContainerUpdate?.(
                    submission.output_container as unknown as ContainerDataType<
                        ContainerType.Custom,
                        CustomDataType
                    >
                )

                setModelResponseExtraInfo(prev => ({
                    ...prev,
                    modelResponseFeedbackCode: feedback.map(f => ({
                        value: f.feedback_result_code,
                        label: f.feedback_result_code_label,
                    })),
                    modelResponse,
                }))

                setModelOutput(prev => {
                    return {
                        ...prev,
                        modelResponse:
                            prev.modelResponse === undefined
                                ? submission.output_container.data.data.response.main_info.value.prompt_revisions?.at(
                                      -1
                                  )?.steps_annotation
                                : prev.modelResponse,
                    }
                })
            },
            onError: message => {
                notificationApi.error({ message, placement: 'topRight' })
            },
            onComplete: hasError => {
                setModelOutput(prev => ({ ...prev, isStreamingCompleted: true, hasError }))
            },
        })
    }

    const previewResponse = useMemo(() => {
        return (
            <MarkdownViewer
                text={modelOutput.modelResponse ?? ''}
                title={'MODEL RESPONSE'}
                isTextStillGenerating={!modelOutput.isStreamingCompleted}
            />
        )
    }, [modelOutput.modelResponse, modelOutput.isStreamingCompleted])

    const incorrectCountMessage = useMemo(() => {
        if (
            isCorrectResponse === false &&
            incorrectResponseCount &&
            incorrectResponseCount < RETRY_LIMIT
        ) {
            return (
                <u className={classes.retryCountMessage}>
                    <b>
                        Model response is incorrect {incorrectResponseCount} out of 5 times. Test
                        model response again.
                    </b>
                </u>
            )
        }
    }, [incorrectResponseCount, isCorrectResponse])

    const disableForm = !!modelOutput.modelResponse && !isCorrectResponse

    const onTestModelResponseBtnClick = () => {
        form.submit()
    }

    return (
        <div className={classes.promptGenerationContainer}>
            {contextHolder}
            <Flex vertical gap={16} className={classes.promptGenerationFormSection}>
                <div>{parse(promptInstruction)}</div>
                <div>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={initialValues}
                        className={classes.inputContainer}
                        onFinish={() => {
                            onTestModelResponse()
                        }}
                        onValuesChange={() => {
                            setIsGeneratedPromptValid(false)
                        }}
                        requiredMark={false}
                        disabled={disableForm}
                    >
                        <Form.Item<PromptGenerationFromContextFormType>
                            className={`${classes.inputField} ${classes['width-50']}`}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please provide the link of the reference document',
                                },
                            ]}
                            name="contextDocumentUrl"
                            label={
                                <p className={classes.required}>Link of the reference document</p>
                            }
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item<PromptGenerationFromContextFormType>
                            className={`${classes.inputField} ${classes['width-50']}`}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please provide the document name',
                                },
                            ]}
                            name="contextDocumentName"
                            label={<p className={classes.required}>Document Name</p>}
                        >
                            <Input />
                        </Form.Item>
                        <div className={`${classes.required} ${classes['page-number-label']}`}>
                            Page Number
                        </div>
                        <Form.List
                            name="contextDocumentPageNumber"
                            rules={[
                                {
                                    validator: async (_, records = []) => {
                                        if (records.length === 0) {
                                            return Promise.reject(
                                                new Error('Please add a page number')
                                            )
                                        }
                                        return Promise.resolve()
                                    },
                                },
                            ]}
                        >
                            {(fields, { add, remove }, { errors }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space
                                            key={key}
                                            style={{ display: 'flex' }}
                                            align="baseline"
                                        >
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'pageNumber']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: 'Page no. is required',
                                                    },
                                                ]}
                                            >
                                                <Input />
                                            </Form.Item>
                                            {!disableForm && (
                                                <MinusCircleOutlined onClick={() => remove(name)} />
                                            )}
                                        </Space>
                                    ))}
                                    <Form.Item
                                        className={`${classes.inputField} ${classes['width-50']}`}
                                    >
                                        <Button
                                            color="red"
                                            onClick={() => add()}
                                            icon={<PlusOutlined />}
                                        >
                                            Add Page No.
                                        </Button>
                                        <Form.ErrorList errors={errors} />
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                        <Form.Item<PromptGenerationFromContextFormType>
                            className={classes.inputField}
                            rules={[{ required: true, message: 'Reference Content is required' }]}
                            name="context"
                            label={<div className={classes.required}>Reference Content</div>}
                        >
                            <TextArea rows={15} />
                        </Form.Item>
                        <Form.Item<PromptGenerationFromContextFormType>
                            className={classes.inputField}
                            rules={[{ required: true, message: 'Question is required' }]}
                            name="prompt"
                            label={<div className={classes.required}>Enter question</div>}
                        >
                            <TextArea rows={6} />
                        </Form.Item>
                        <Flex className={`${classes.previewContainer}`}>
                            <MarkdownPreviewComponent content={promptFormValue} />
                        </Flex>
                    </Form>
                    <PlagiarismChecker
                        installedPlugins={installedPlugins}
                        form={form}
                        onPlagiarismTestResult={(val: boolean) => setIsPlagiarismCheckPassed(val)}
                        onPlagiarismScan={() => {
                            setModelOutput({
                                modelFeedback: [],
                                modelResponse: undefined,
                                isStreamingCompleted: false,
                                hasError: false,
                            })
                        }}
                    />
                    <ActionButton
                        disabled={
                            !isPlagiarismCheckPassed ||
                            (!!modelOutput.modelResponse && !modelOutput.isStreamingCompleted) ||
                            (incorrectResponseCount === RETRY_LIMIT &&
                                isCorrectResponse === false) ||
                            (!!modelOutput.modelResponse &&
                                modelOutput.isStreamingCompleted &&
                                isCorrectResponse === undefined)
                        }
                        color="primary"
                        onClick={onTestModelResponseBtnClick}
                        title="Test Model Response"
                        style={{ margin: '15px 0', borderRadius: '4px' }}
                    />
                </div>
            </Flex>

            {fetchingModelResponse && (
                <Loader
                    title={"Generating model response may take up to 3 minutes. Don't refresh. "}
                    width="480px"
                />
            )}
            {modelOutput.modelResponse !== undefined && (
                <Flex gap={16} vertical>
                    <div className={classes.separator} />
                    {previewResponse}
                    {modelOutput.isStreamingCompleted && !modelOutput.hasError && (
                        <Flex gap={16} vertical>
                            <div className={classes.separator} />
                            <ModelFeedback feedbacks={modelOutput.modelFeedback} />
                            <div className={classes.separator} />
                            <Flex gap={16} vertical>
                                <p className={classes.title}>FILL THE BELOW DETAILS</p>
                                <div className={classes.required}>
                                    Is the final model response incorrect?
                                </div>
                                <Radio.Group
                                    onChange={e => {
                                        setIsCorrectResponse(e.target.value)
                                        if (incorrectResponseCount === RETRY_LIMIT) {
                                            setIsGeneratedPromptValid(e.target.value === false)
                                        }
                                    }}
                                    value={isCorrectResponse}
                                >
                                    <Radio value={false}>Yes</Radio>
                                    <Radio value={true}>No</Radio>
                                </Radio.Group>
                            </Flex>
                            {incorrectCountMessage}
                            {isCorrectResponse && (
                                <Flex gap={5} className={classes.tryAnotherPrompt}>
                                    <InfoCircleFilled className={classes.icon} />
                                    <div>
                                        Please go to the top prompt section and try another prompt
                                        which would most likely generate an incorrect answer
                                    </div>
                                </Flex>
                            )}
                        </Flex>
                    )}
                </Flex>
            )}
        </div>
    )
}
